import { AnimationEvent, ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha, Box, Button, CircularProgress, Icon, Typography, styled } from "@mui/material";
import { keyframes } from "@mui/system";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

import useNotifications from "./NotificationsContext";
import NotificationItem, { DismissSource } from "./NotificationItem";
import useExitingRows from "./useExitingRows";
import useFlip from "./useFlip";
import {
  EASE_ENTRANCE,
  EASE_IN_OUT,
  EXIT_OFFSCREEN,
  hoverTint,
  prefersReducedMotion,
  REVEAL_DISTANCE,
  REVEAL_MS,
  ROW_ENTER_DISTANCE,
  ROW_ENTER_MAX,
  ROW_ENTER_MS,
  STAGGER_MAX_STEPS,
  STAGGER_MS,
  TEXT_SWAP_DISTANCE,
  TEXT_SWAP_MS,
} from "./motion";
import { DIVIDER, inset, INSET_VAR, ROW_HOVER, TEXT_MUTED } from "./palette";
import { iconForType, isUrgent, Notification } from "./types";
import { AppRoutes } from "../../config";

// The panel no longer has a mount animation of its own: open/close is one
// transition on the Popover paper (PanelTransition). Everything below only
// animates things that change *while the panel is open*.

// Empty state after Clear all / the last dismiss: the texts-reveal recipe. Icon,
// headline and subline rise --distance-medium (12px) with a --duration-stagger
// (40ms) offset, so the eye lands on the icon first; 2 × 40ms keeps the cascade far
// inside the ~300ms stagger budget. Recipe blur dropped (opacity + transform only);
// project entrance curve instead of the recipe's, per CLAUDE.md.
const feedReveal = keyframes`
  0%   { opacity: 0; transform: translate3d(0, ${REVEAL_DISTANCE}px, 0); }
  100% { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// Urgent banner arriving while open: same drop-in as a new row (it's announcing one).
const bannerEnter = keyframes`
  0%   { opacity: 0; transform: translate3d(0, -${ROW_ENTER_DISTANCE}px, 0); }
  100% { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// Wallet page: "See all transactions" deep-links there (same route the BottomNavBar
// account button uses).
const WALLET_PATH = AppRoutes.Profile.subRoutes.Dashboard.path;

interface NotificationPanelProps {
  /** Called after navigating to an entity so the host can close the popover. */
  onNavigate?: () => void;
  /** Mobile only: enable swipe-left-to-clear per row (desktop keeps the hover ×). */
  swipeToDismiss?: boolean;
}

export const NotificationPanel = ({ onNavigate, swipeToDismiss }: NotificationPanelProps) => {
  const navigate = useNavigate();
  const { items, loading, hasMore, page, loadPage, markRead, dismiss, dismissAll } = useNotifications();

  const panelRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Things that mount after the panel's first commit (rows pushed in, the empty
  // state after Clear all, a new urgent banner) animate in; what the panel opens
  // with doesn't, since the popover's own open already is the entrance. Read at the
  // moment each child mounts, and each child freezes it in state.
  const hasMounted = useRef(false);
  useEffect(() => {
    hasMounted.current = true;
  }, []);

  // FLIP capture, taken right before any change that frees a slot.
  const captureLayout = useFlip(panelRef);
  // Rows keep rendering (as read-only ghosts) until their exit ends; the context and
  // the badge have already moved on.
  const { displayItems, exitRows, exitDelayOf } = useExitingRows(items, captureLayout);

  const isEmpty = displayItems.length === 0;

  // Which rows animate in: only ones *prepended* since the last commit (SSE push,
  // poller refetch, a failed dismiss restored at the head), i.e. new ids above every
  // row already on screen, capped at what the panel can show. Load more appends
  // below, off-screen, so those rows just appear.
  const shownIds = useRef(new Set(displayItems.map((n) => n.id)));
  useLayoutEffect(() => {
    shownIds.current = new Set(displayItems.map((n) => n.id));
  }, [displayItems]);
  const firstShown = displayItems.findIndex((n) => shownIds.current.has(n.id));
  const enterBefore = Math.min(firstShown === -1 ? displayItems.length : firstShown, ROW_ENTER_MAX);

  // Derived from what's on screen (ghosts included) rather than the context's copy,
  // so the banner for an urgent row leaves in the same commit as that row's ghost
  // instead of snapping away while the row is still animating out.
  const urgentNotification = useMemo(
    () => displayItems.find((n) => isUrgent(n.type) && !n.isRead) ?? null,
    [displayItems]
  );

  const handleRead = (id: string) => {
    // Reading the urgent row removes the banner above the list: glide the rows up.
    if (urgentNotification?.id === id) {
      captureLayout();
    }
    markRead(id);
  };

  const handleDismiss = (id: string, source: DismissSource) => {
    if (source === "button" && !prefersReducedMotion) {
      // × dismiss: the row plays its exit, then leaves (FLIP captured then).
      exitRows(new Map([[id, 0]]));
    } else {
      // Swipe commit: the gesture already flung the row off-screen, so drop it
      // now and let the rows below glide up. Reduced motion: immediate.
      captureLayout();
    }
    // Optimistic mutation first, always — the animation only delays DOM removal.
    dismiss(id);
  };

  const handleClearAll = () => {
    const list = listRef.current;
    if (prefersReducedMotion || !list) {
      dismissAll();
      return;
    }
    // Cascade from the first row the user can actually see (the list may be
    // scrolled), 40ms apart, capped at 7 steps (280ms) to stay inside the stagger
    // budget. Rows scrolled out of view don't animate: they hold their slot hidden
    // until the batch is dropped. One read pass of rects, on click, not per frame.
    const viewport = list.getBoundingClientRect();
    const rows = Array.from(list.querySelectorAll<HTMLElement>("[data-notif-row]")).map((row) => ({
      id: row.dataset.flipId,
      rect: row.getBoundingClientRect(),
    }));
    const liveIds = new Set(items.map((n) => n.id));
    const delays = new Map<string, number>();
    let step = 0;
    rows.forEach(({ id, rect }) => {
      // Rows already exiting keep their own timing.
      if (!id || !liveIds.has(id)) {
        return;
      }
      const onScreen = rect.bottom > viewport.top && rect.top < viewport.bottom;
      if (!onScreen) {
        delays.set(id, EXIT_OFFSCREEN);
        return;
      }
      delays.set(id, Math.min(step, STAGGER_MAX_STEPS) * STAGGER_MS);
      step += 1;
    });
    exitRows(delays);
    dismissAll();
  };

  const handleSeeAll = () => {
    navigate(WALLET_PATH);
    onNavigate?.();
  };

  return (
    <Panel ref={panelRef}>
      {/* The Figma has no header bar — the panel closes on outside click (handled
          by the host Popover/Drawer). Everything below scrolls. */}
      <List ref={listRef}>
        {/* `overflow: clip` wrapper: row enter/exit and FLIP transforms stay inside
            it, so a row mid-glide never grows the scroll area or flashes a scrollbar. */}
        <ListContent>
          {/* Pinned banner for the most recent unread urgent notification. */}
          {urgentNotification && (
            <UrgentBannerView key={urgentNotification.id} animate={hasMounted.current}>
              <span className="banner-badge">
                <Icon component={iconForType(urgentNotification.type)} sx={{ fontSize: 20 }} />
              </span>
              <Box sx={{ minWidth: 0 }}>
                <Typography className="banner-title">{urgentNotification.title}</Typography>
                <Typography className="banner-body">{urgentNotification.body}</Typography>
              </Box>
            </UrgentBannerView>
          )}

          {isEmpty && !loading && <EmptyFeed animate={hasMounted.current} />}

          {displayItems.map((notification: Notification, index) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onRead={handleRead}
              onDismiss={handleDismiss}
              onNavigate={onNavigate}
              swipeToDismiss={swipeToDismiss}
              animateIn={hasMounted.current && index < enterBefore}
              exitDelay={exitDelayOf(notification.id)}
            />
          ))}

          {isEmpty && loading && (
            <LoadingRow>
              <CircularProgress size={20} sx={{ color: TEXT_MUTED }} />
            </LoadingRow>
          )}

          {hasMore && (
            <LoadMore
              data-flip-id="load-more"
              className={loading ? "is-loading" : undefined}
              disableRipple
              onClick={() => loadPage(page + 1)}
              disabled={loading}
            >
              {/* Both labels share one grid cell (stable width) and cross-swap:
                  the text-swap recipe, minus its blur. */}
              <SwapLabel>
                <span className="swap-idle" aria-hidden={loading}>
                  Load more
                </span>
                <span className="swap-busy" aria-hidden={!loading}>
                  Loading…
                </span>
              </SwapLabel>
            </LoadMore>
          )}
        </ListContent>
      </List>

      {/* Footer: "See all transactions" is always there (it's the way out to the
          full history, empty feed or not); "Clear all" only while there are rows.
          Bulk mark-as-read is intentionally not offered here any more — markAllRead
          still exists on the context. */}
      {/* Figma: two equal-weight pill actions split by a 44px rule. With the feed
          empty only "See all transactions" remains, centred on its own. */}
      <Footer className={isEmpty ? "single" : undefined}>
        <TextAction data-flip-id="see-all" disableRipple onClick={handleSeeAll}>
          See all transactions
        </TextAction>
        {!isEmpty && (
          <>
            <FooterDivider />
            {/* Disabled as soon as the context is empty, i.e. while the cascade runs. */}
            <TextAction disableRipple onClick={handleClearAll} disabled={items.length === 0}>
              Clear all
            </TextAction>
          </>
        )}
      </Footer>
    </Panel>
  );
};

// Plays `className`'s one-shot reveal only if the element mounted after the panel's
// first commit (`animate` is frozen at mount), and drops the class on the last
// animationend so nothing stays on its own layer afterwards.
const useMountReveal = (animate: boolean, isLast: (event: AnimationEvent<HTMLElement>) => boolean) => {
  const [playing, setPlaying] = useState(animate && !prefersReducedMotion);
  return {
    className: playing ? "is-revealing" : undefined,
    onAnimationEnd: (event: AnimationEvent<HTMLElement>) => {
      if (playing && isLast(event)) {
        setPlaying(false);
      }
    },
  };
};

const UrgentBannerView = ({ animate, children }: { animate: boolean; children: ReactNode }) => {
  const reveal = useMountReveal(animate, (event) => event.target === event.currentTarget);
  return (
    <UrgentBanner role="alert" {...reveal}>
      {children}
    </UrgentBanner>
  );
};

const EmptyFeed = ({ animate }: { animate: boolean }) => {
  // The subline is last in the cascade, so its animationend closes the reveal.
  const reveal = useMountReveal(animate, (event) => event.target === event.currentTarget.lastElementChild);
  return (
    <EmptyState {...reveal}>
      {/* Not in the Figma: built from the row's parts (36px grey badge, 14px
          medium title, muted body) so it reads as the same panel. */}
      <span className="empty-badge">
        <Icon component={NotificationsNoneOutlinedIcon} sx={{ fontSize: 20 }} />
      </span>
      {/* Stagger offsets inline: they only matter while the reveal class is on. */}
      <Typography className="empty-title" style={{ animationDelay: `${STAGGER_MS}ms` }}>
        You&apos;re all caught up
      </Typography>
      <Typography className="empty-body" style={{ animationDelay: `${STAGGER_MS * 2}ms` }}>
        No notifications yet
      </Typography>
    </EmptyState>
  );
};

// Figma: 547px wide, #f2f2f2 (the theme's back.infoCard), 20px radius. Content
// sits 36px in from both edges; below sm the inset tightens to 20px so a 375px
// screen keeps a usable text column. Rows, dividers, banner and footer all read
// the inset from one custom property.
const Panel = styled(Box)(({ theme }) => ({
  [INSET_VAR]: "36px",
  position: "relative",
  width: "min(547px, calc(100vw - 24px))",
  maxHeight: "min(600px, calc(100vh - 120px))",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.back.infoCard,
  borderRadius: "20px",
  overflow: "hidden",

  [theme.breakpoints.down("sm")]: {
    [INSET_VAR]: "20px",
  },

  ...theme.applyStyles("dark", {
    backgroundColor: "#1a1a1a",
  }),
}));

// Footer actions (Figma "Component 11"): 44px pills, Inter Bold 14/28, black
// (index.html registers Inter-Bold at 600; 700 would fall through to Black). On
// hover the pill fills black and the label turns white at regular weight. MUI
// Button transitions background-color and color by default; here the fill is an
// opacity-faded ::before (hoverTint) and the label's colour/weight change is
// instant, so the only things that ever animate are opacity and transform.
// Keyboard focus shows the same filled pill.
const TextAction = styled(Button)(({ theme }) => {
  const filled = {
    color: theme.palette.common.white,
    fontWeight: 400,
    ...theme.applyStyles("dark", { color: theme.palette.common.black }),
  };
  const tint = hoverTint(theme, { light: theme.palette.common.black, dark: theme.palette.common.white });
  return {
    width: "100%",
    minWidth: 0,
    minHeight: "44px",
    padding: "8px 24px",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "28px",
    textTransform: "none",
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "20px",
    whiteSpace: "nowrap",
    transition: "none",
    ...tint,
    "&:hover": {
      backgroundColor: "transparent",
      border: "none",
    },
    // Merged into hoverTint's own (hover: hover) block; a second key would replace it.
    "@media (hover: hover)": {
      ...(tint["@media (hover: hover)"] as object),
      "&:hover": filled,
    },
    "&.Mui-focusVisible": filled,
    "&.Mui-focusVisible::before": { opacity: 1 },
    "&.Mui-disabled": {
      color: "#bdbdbd",
      backgroundColor: "transparent",
      border: "none",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "8px 12px",
    },
    ...theme.applyStyles("dark", {
      "&.Mui-disabled": { color: "rgba(255, 255, 255, 0.3)" },
    }),
  };
});

// Pinned urgent notification. Not in the Figma; it takes the slot and layout of
// the design's pinned top alert (flush, full width, 36px badge, 14px type) and
// keeps its warning accent (theme warning.main) as a tint, a 3px edge and the badge.
const UrgentBanner = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "14px",
  padding: `24px ${inset}`,
  backgroundColor: alpha(theme.palette.warning.main, 0.12),
  boxShadow: `inset 3px 0 0 0 ${theme.palette.warning.main}`,
  "& .banner-badge": {
    flexShrink: 0,
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(theme.palette.warning.main, 0.24),
    color: theme.palette.warning.main,
  },
  "& .banner-title": {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "21px",
    color: theme.palette.text.primary,
  },
  "& .banner-body": {
    marginTop: "2px",
    fontSize: "14px",
    lineHeight: "17px",
    color: TEXT_MUTED,
    ...theme.applyStyles("dark", { color: "rgba(255, 255, 255, 0.6)" }),
  },
  "&.is-revealing": {
    willChange: "transform, opacity",
    animation: `${bannerEnter} ${ROW_ENTER_MS}ms ${EASE_ENTRANCE} both`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&.is-revealing": { animation: "none" },
  },
  ...theme.applyStyles("dark", {
    backgroundColor: alpha(theme.palette.warning.main, 0.18),
  }),
}));

const List = styled(Box)({
  flex: 1,
  overflowY: "auto",
  overscrollBehavior: "contain",
});

// `clip` (not `hidden`): clips without becoming a second scroll container, and
// stops transformed descendants from contributing to List's scrollable overflow.
const ListContent = styled(Box)({
  overflow: "clip",
});

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: `48px ${inset}`,
  "& .empty-badge": {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.border.default,
    color: "#000",
    ...theme.applyStyles("dark", {
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      color: "rgba(255, 255, 255, 0.85)",
    }),
  },
  "& .empty-title": {
    marginTop: "14px",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "21px",
    color: theme.palette.text.primary,
  },
  "& .empty-body": {
    marginTop: "2px",
    fontSize: "14px",
    lineHeight: "17px",
    color: TEXT_MUTED,
    ...theme.applyStyles("dark", { color: "rgba(255, 255, 255, 0.6)" }),
  },
  "&.is-revealing > *": {
    willChange: "transform, opacity",
    animation: `${feedReveal} ${REVEAL_MS}ms ${EASE_ENTRANCE} both`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&.is-revealing > *": { animation: "none" },
  },
}));

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: "32px",
});

// Figma: an inset hairline (the same #dedede as the row dividers) closes the feed,
// then 29px / 44px of actions / 31px. The two cells keep the design's 226:197
// split but never shrink below their label, so a 375px screen doesn't clip
// "See all transactions".
const Footer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(max-content, 226fr) auto minmax(max-content, 197fr)",
  alignItems: "center",
  columnGap: "28px",
  flexShrink: 0,
  margin: `0 ${inset}`,
  padding: "29px 0 31px",
  borderTop: `1px solid ${DIVIDER}`,
  "&.single": {
    gridTemplateColumns: "minmax(max-content, 226px)",
    justifyContent: "center",
  },
  [theme.breakpoints.down("sm")]: {
    columnGap: "12px",
    padding: "16px 0 20px",
  },
  ...theme.applyStyles("dark", {
    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
  }),
}));

// Figma "Line 131": a 44px vertical rule in #ccc (the theme's border.default).
const FooterDivider = styled("span")(({ theme }) => ({
  width: "1px",
  height: "44px",
  backgroundColor: theme.palette.border.default,
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, 0.16)",
  }),
}));

const LoadMore = styled(Button)(({ theme }) => ({
  display: "flex",
  // Not in the Figma: a quieter sibling of the footer pills (same 14px bold label
  // and 20px radius, muted until hovered, row-hover tint).
  margin: "8px auto 16px",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "28px",
  textTransform: "none",
  color: TEXT_MUTED,
  backgroundColor: "transparent",
  border: "none",
  borderRadius: "20px",
  padding: "8px 24px",
  transition: "none",
  ...hoverTint(theme, { light: ROW_HOVER, dark: "rgba(255, 255, 255, 0.06)" }),
  "&:hover": {
    backgroundColor: "transparent",
    border: "none",
    color: theme.palette.text.primary,
  },

  // Load more ↔ Loading… text swap (--duration-quick, --distance-micro, ease-in-out,
  // symmetric): the outgoing label lifts 4px and fades while the incoming one
  // settles from 4px below.
  "& .swap-idle, & .swap-busy": {
    transition: `opacity ${TEXT_SWAP_MS}ms ${EASE_IN_OUT}, transform ${TEXT_SWAP_MS}ms ${EASE_IN_OUT}`,
  },
  "& .swap-busy": {
    opacity: 0,
    transform: `translate3d(0, ${TEXT_SWAP_DISTANCE}px, 0)`,
  },
  "&.is-loading .swap-idle": {
    opacity: 0,
    transform: `translate3d(0, -${TEXT_SWAP_DISTANCE}px, 0)`,
  },
  "&.is-loading .swap-busy": {
    opacity: 1,
    transform: "translate3d(0, 0, 0)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .swap-idle, & .swap-busy": { transition: "none" },
  },
}));

// One grid cell for both labels, so the button keeps the wider label's width and
// nothing reflows when the text swaps.
const SwapLabel = styled("span")({
  display: "inline-grid",
  "& > span": { gridArea: "1 / 1" },
});

export default NotificationPanel;
